/**
 * スポット単位の滞在時間・到着希望の設定
 *
 * 常設シートの中身として差し替えて使う。地図を背面に残したまま編集させたいので
 * それ自体はシートを持たない。
 *
 * 到着希望はハード制約にすると解なしで丸ごと外されるため、バックエンドでは
 * ソフト制約として渡している。ここでも「必ず守られる」とは書かない。
 */

import { useState } from 'react'

import type { DesiredArrivalMode, RoutePlanItem, UpdateRoutePlanItemParams } from '@/api/types/route-plan'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useSubmitLock } from '@/hooks/useSubmitLock'

import { buildDepartAt, toTimeInput } from '../../lib'

const STAY_PRESETS = [15, 30, 45, 60, 90, 120]

const ARRIVAL_MODES: { value: DesiredArrivalMode; label: string }[] = [
  { value: 'until', label: 'までに' },
  { value: 'at', label: 'ごろ' },
  { value: 'after', label: '以降' },
]

type Props = {
  item: RoutePlanItem
  /** 希望時刻を日付付きに組み立てるための基準日（YYYY-MM-DD） */
  planDate: string
  /** 出発地・到着地に充てたスポットは shipment に入らず、到着希望が効かない */
  isPinned: boolean
  onSubmit: (item: RoutePlanItem, params: UpdateRoutePlanItemParams) => Promise<void>
}

/** 別のスポットに切り替わっても入力を入れ直せるよう、呼び出し側は place_id を key に渡す */
export const PlanItemPanel = ({ item, planDate, isPinned, onSubmit }: Props) => {
  const [stayMinutes, setStayMinutes] = useState(item.stay_minutes)
  const [arrivalTime, setArrivalTime] = useState(() => toTimeInput(item.desired_arrival_at))
  const [arrivalMode, setArrivalMode] = useState<DesiredArrivalMode>(item.desired_arrival_mode ?? 'until')
  const [isRequired, setIsRequired] = useState(item.is_required)
  const { isSubmitting, run } = useSubmitLock()

  const canUseArrival = !isPinned && planDate !== ''

  const submit = async (params: UpdateRoutePlanItemParams) => {
    await run(() => onSubmit(item, params))
  }

  const handleSave = () => {
    const params: UpdateRoutePlanItemParams = { stay_minutes: stayMinutes }
    if (!isPinned) {
      params.is_required = isRequired
    }
    if (canUseArrival) {
      // 時刻を空にしたら希望そのものを解除する
      params.desired_arrival_at = arrivalTime ? buildDepartAt(planDate, arrivalTime) : null
      params.desired_arrival_mode = arrivalTime ? arrivalMode : null
    }
    void submit(params)
  }

  return (
    <div className="space-y-5 px-4 pt-4 pb-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="item-required">必ず立ち寄る</Label>
          <Switch
            id="item-required"
            checked={isPinned || isRequired}
            disabled={isPinned}
            onCheckedChange={setIsRequired}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {isPinned
            ? '出発地・到着地に充てたスポットは常に順路に入ります'
            : '上限時間に収まらないときは、他のスポットを先に外してここを残します。反映するには再最適化が必要です'}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="item-stay">滞在時間（分）</Label>
        <Input
          id="item-stay"
          type="number"
          min={0}
          max={600}
          value={stayMinutes}
          onChange={(e) => setStayMinutes(Number(e.target.value))}
        />
        <div className="flex flex-wrap gap-2">
          {STAY_PRESETS.map((minutes) => (
            <Button
              key={minutes}
              type="button"
              size="sm"
              variant={stayMinutes === minutes ? 'default' : 'outline'}
              className="px-3!"
              onClick={() => setStayMinutes(minutes)}
            >
              {minutes}分
            </Button>
          ))}
        </div>
        {item.stay_minutes_overridden && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="px-2! text-xs text-muted-foreground"
            disabled={isSubmitting}
            onClick={() => void submit({ reset_stay_minutes: true })}
          >
            既定の滞在時間に戻す
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="item-arrival">到着したい時刻</Label>
        <div className="flex gap-2">
          <Input
            id="item-arrival"
            type="time"
            className="min-w-0 flex-1"
            disabled={!canUseArrival}
            value={arrivalTime}
            onChange={(e) => setArrivalTime(e.target.value)}
          />
          {arrivalTime && (
            <Button
              type="button"
              variant="ghost"
              className="shrink-0 px-3! text-muted-foreground"
              disabled={!canUseArrival}
              onClick={() => setArrivalTime('')}
            >
              クリア
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {ARRIVAL_MODES.map((mode) => (
            <Button
              key={mode.value}
              type="button"
              variant={arrivalMode === mode.value ? 'default' : 'outline'}
              className="flex-1 px-2!"
              disabled={!canUseArrival || !arrivalTime}
              onClick={() => setArrivalMode(mode.value)}
            >
              {mode.label}
            </Button>
          ))}
        </div>
        {isPinned ? (
          <p className="text-xs text-muted-foreground">
            出発地・到着地に充てたスポットは順路の端に固定されるため、到着希望は使えません
          </p>
        ) : planDate === '' ? (
          <p className="text-xs text-muted-foreground">先に条件で日付と出発時刻を決めてください</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            希望はできるだけ守る目安として扱います。営業時間や移動時間と両立しないときはずれます。反映するには再最適化が必要です
          </p>
        )}
      </div>

      <Button className="w-full" disabled={isSubmitting} onClick={handleSave}>
        保存
      </Button>
    </div>
  )
}
